import { FileText } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ClipboardList, Plus, Sparkles, Trash2, Truck, XCircle } from 'lucide-react';
import { PERMISSIONS, PURCHASE_ORDER_STATUS_LABELS, formatDate, formatMoney, formatQty } from '@erp/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader, ErrorState, EmptyState, StatCard } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { StatusBadge } from '@/components/ui/badge';
import { DataTable, Pagination, type Column } from '@/components/ui/table';
import { FloatingPanel } from '@/components/ui/floating-panel';
import { Field, Input, Select } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { fechaPrevista } from '@/lib/fecha-prevista';
import { CurrencyField } from '@/components/ui/currency-field';
import { limpiarMoneda, proponerMoneda } from '@/lib/moneda-propuesta';
import { toast } from 'sonner';
import { SuggestionsPanel } from './SuggestionsPanel';

interface OrderRow {
  id: string; number: string; status: string; expected_date: string | null; created_at: string;
  supplier_name: string; line_count: number; total: number; pending_qty: number;
}
interface SupplierRow {
  id: string; name: string; country: string | null;
  /** Valores por defecto de la ficha: se proponen al crear el pedido y se pueden cambiar. */
  payment_method_id?: string | null; lead_time_days?: number | null; currency?: string;
}
interface ProductRow { id: string; sku: string; name: string; base_unit: string | null }
interface LineRow {
  id: string; productId: string; quantity: number; unitCost: number; receivedQuantity: number;
  sku: string | null; productName: string | null;
  baseUnit: string | null; purchaseUnit: string | null; purchaseToBase: number;
}
interface OrderDetail {
  id: string; number: string; status: string; supplierName: string; expectedDate: string | null;
  notes: string | null; lines: LineRow[];
  /** Forma de pago acordada; si está vacía, la que tenga el proveedor en su ficha. */
  paymentMethodId: string | null;
  paymentMethodName: string | null;
  /** Moneda del pedido y tipo **fijado el día en que se creó** (euros por una unidad). */
  currency?: string | null;
  exchangeRate?: string | null;
}

const FILTROS: [string, string][] = [
  ['', 'Todos'],
  ...Object.entries(PURCHASE_ORDER_STATUS_LABELS).map(([value, label]) => [value, label] as [string, string])
];
const ESTADOS_RECIBIBLES = ['draft', 'confirmed', 'partially_received'];
/** Un proveedor de fuera de la UE sí pasa aduana; uno de la UE, no. */
const UE = new Set(['ES', 'PT', 'FR', 'DE', 'IT', 'NL', 'BE', 'LU', 'IE', 'DK', 'SE', 'FI', 'AT', 'PL', 'CZ', 'SK', 'HU', 'SI', 'HR', 'RO', 'BG', 'GR', 'CY', 'MT', 'EE', 'LV', 'LT']);

/**
 * Pedidos de compra: listado, alta y recepción de mercancía. Se llaman así, y no «pedidos a
 * proveedores», para no confundirlos con los pedidos de venta de clientes.
 *
 * El backend ya tenía el ciclo completo (crear, detalle y recibir con entrada a stock vía
 * `moveStock`); faltaba la pantalla, así que compras trabajaba por API.
 */
export function PurchaseOrdersPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);

  const canWrite = can(PERMISSIONS.PURCHASES_WRITE);
  const canReadSuppliers = can(PERMISSIONS.SUPPLIERS_READ);
  const canReadProducts = can(PERMISSIONS.PRODUCTS_READ);
  const canReceive = can(PERMISSIONS.PURCHASES_VALIDATE);

  const [page, setPage] = useState(1);
  const ordersQuery = useQuery({
    // El listado está paginado: sin la página en la clave, cambiar de página no pediría datos nuevos.
    queryKey: ['purchase-orders', status, page],
    queryFn: () => api<{ data: OrderRow[]; meta: { page: number; pageSize: number; total: number; totalPages: number } }>(
      `/purchase-orders?page=${page}${status ? `&status=${status}` : ''}`),
    placeholderData: keepPreviousData
  });
  const metaOrders = ordersQuery.data?.meta;
  // Si al recibir o cancelar el último pedido de la página esta queda vacía, se vuelve a la anterior.
  useEffect(() => {
    if (ordersQuery.isSuccess && !ordersQuery.isFetching && page > 1 && (ordersQuery.data?.data.length ?? 0) === 0) setPage(page - 1);
  }, [ordersQuery.isSuccess, ordersQuery.isFetching, ordersQuery.data, page]);
  // Resumen sin filtro: del listado filtrado saldría a cero en cuanto se filtre «Recibido».
  // Agregados del servidor: antes se calculaban sobre la lista completa, que ya no se pide.
  const resumenQuery = useQuery({
    queryKey: ['purchase-orders', 'resumen'],
    queryFn: () => api<{ data: { open_count: number; pending_qty: number; amount: number } }>('/purchase-orders/summary')
  });
  const suppliersQuery = useQuery({
    queryKey: ['suppliers', 'options'],
    queryFn: () => api<{ data: SupplierRow[] }>('/suppliers/options'),
    enabled: canReadSuppliers
  });
  const productsQuery = useQuery({
    queryKey: ['products', 'purchase'],
    queryFn: () => api<{ data: ProductRow[] }>('/products?pageSize=200&active=true'),
    enabled: canReadProducts
  });

  const orders = ordersQuery.data?.data ?? [];
  const resumen = resumenQuery.data?.data ?? { open_count: 0, pending_qty: 0, amount: 0 };

  const columns: Column<OrderRow>[] = useMemo(() => [
    {
      key: 'number',
      header: 'Pedido',
      cell: (row) => (
        <div>
          <p className="font-mono font-medium">{row.number}</p>
          <p className="text-[10px] text-muted-foreground">{formatDate(row.created_at)}</p>
        </div>
      )
    },
    { key: 'supplier', header: 'Proveedor', cell: (row) => <span className="font-medium">{row.supplier_name}</span> },
    { key: 'status', header: 'Estado', cell: (row) => <StatusBadge status={row.status} /> },
    {
      key: 'expected',
      header: 'Prevista',
      cell: (row) => <span className="text-xs">{row.expected_date ? formatDate(row.expected_date) : '—'}</span>
    },
    {
      key: 'lines',
      header: 'Líneas',
      className: 'text-right',
      cell: (row) => <span className="tabular-nums">{row.line_count}</span>
    },
    {
      key: 'pending',
      header: 'Pendiente',
      className: 'text-right',
      cell: (row) => (
        <span className={row.pending_qty > 0 ? 'font-medium tabular-nums text-amber-600' : 'tabular-nums text-muted-foreground'}>
          {formatQty(row.pending_qty)}
        </span>
      )
    },
    {
      key: 'total',
      header: 'Importe',
      className: 'text-right',
      cell: (row) => <span className="tabular-nums">{formatMoney(row.total)}</span>
    },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="icon-sm" title="PDF"
            onClick={(e) => { e.stopPropagation(); window.open(`/api/purchase-orders/${row.id}/pdf`, '_blank', 'noopener'); }}>
            <FileText />
          </Button>
          <Button variant={openId === row.id ? 'outline' : 'ghost'} size="sm" onClick={() => setOpenId(openId === row.id ? null : row.id)}>
            {openId === row.id ? 'Cerrar' : 'Abrir'}
          </Button>
        </div>
      )
    }
  ], [openId]);

  return (
    <div>
      {/* Durante el alta no se muestran los botones de cabecera: el diálogo del MRP vive en la vista
          de lista, así que abrirlo aquí lo dejaría pendiente al cancelar. */}
      <PageHeader
        title="Pedidos de compra"
        description="Compras de material, incluidas las importaciones, y recepción en almacén"
        actions={canWrite && !creating ? (
          <>
            <Button size="sm" variant="outline" onClick={() => setSuggestOpen(true)}>
              <Sparkles /> Sugerir del MRP
            </Button>
            <Button size="sm" disabled={!canReadSuppliers || !canReadProducts} onClick={() => { setCreating(true); setOpenId(null); }}>
              <Plus /> Nuevo pedido
            </Button>
          </>
        ) : undefined}
      />

      {/* Al dar de alta un pedido la pantalla queda solo para el alta: no tiene sentido enseñar los
          pedidos anteriores mientras se rellena uno nuevo. */}
      {creating && canWrite ? (
        <CreateOrderPanel
          suppliers={suppliersQuery.data?.data ?? []}
          products={productsQuery.data?.data ?? []}
          suppliersError={suppliersQuery.error as Error | null}
          productsError={productsQuery.error as Error | null}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); void qc.invalidateQueries({ queryKey: ['purchase-orders'] }); }}
        />
      ) : (
      <>
      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <StatCard label="Pedidos abiertos" value={String(resumen.open_count)} hint="Sin recibir del todo" icon={ClipboardList} />
        <StatCard label="Unidades pendientes" value={formatQty(resumen.pending_qty)} hint="Por llegar a almacén" icon={Truck} tone="warning" />
        <StatCard
          label="Importe de los pedidos abiertos"
          value={formatMoney(resumen.amount)}
          hint="Total del pedido, no solo lo pendiente"
          tone="neutral"
          icon={ClipboardList}
        />
      </div>

      <SuggestionsPanel
        open={suggestOpen}
        onOpenChange={setSuggestOpen}
        onCreated={() => {
          void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
          // Un pedido nuevo cambia lo que falta: la propuesta del MRP se recalcula en vivo.
          void qc.invalidateQueries({ queryKey: ['purchase-suggestions'] });
        }}
      />


      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          {FILTROS.map(([value, label]) => (
            <Button
              key={value || 'all'} size="sm" variant={status === value ? 'default' : 'outline'}
              aria-pressed={status === value}
              onClick={() => { setStatus(value); setPage(1); setOpenId(null); }}
            >
              {label}
            </Button>
          ))}
          <span className="ml-auto text-[11px] text-muted-foreground">{metaOrders?.total ?? orders.length} pedidos</span>
        </div>
        {ordersQuery.error ? (
          <ErrorState message={(ordersQuery.error as Error).message} onRetry={() => void ordersQuery.refetch()} />
        ) : (
          <DataTable
            columns={columns}
            rows={orders}
            loading={ordersQuery.isLoading}
            rowKey={(row) => row.id}
            empty={<EmptyState title="Sin pedidos" description="Crea un pedido de compra para empezar." icon={ClipboardList} />}
          />
        )}
        {metaOrders && metaOrders.totalPages > 1 ? (
          <Pagination page={metaOrders.page} totalPages={metaOrders.totalPages}
            total={metaOrders.total} onPageChange={setPage} />
        ) : null}
      </Card>
      </>
      )}

      {/* El detalle se abre en ventana flotante sobre el listado: pintado debajo, con 50 pedidos por
          delante nadie llega a verlo. */}
      {openId && !creating ? (
        <FloatingPanel
          open
          onClose={() => setOpenId(null)}
          title="Pedido de compra"
          description="Se puede mover arrastrando la cabecera; la lista de detrás sigue visible"
          width={760}
        >
        <OrderDetailPanel
          key={openId}
          orderId={openId}
          canReceive={canReceive}
          canWrite={canWrite}
          onChanged={() => {
            void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
            // La recepción mueve stock y actualiza el coste del artículo.
            void qc.invalidateQueries({ queryKey: ['stock'] });
            void qc.invalidateQueries({ queryKey: ['products'] });
          }}
        />
        </FloatingPanel>
      ) : null}
    </div>
  );
}

function CreateOrderPanel({ suppliers, products, suppliersError, productsError, onClose, onSaved }: {
  suppliers: SupplierRow[]; products: ProductRow[];
  suppliersError: Error | null; productsError: Error | null;
  onClose: () => void; onSaved: () => void;
}) {
  const [supplierId, setSupplierId] = useState('');
  const [currency, setCurrency] = useState('EUR');
  // Lo último que propuso la pantalla, para no pisar lo que el usuario haya elegido.
  const [propuestaMoneda, setPropuestaMoneda] = useState('EUR');
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState([{ productId: '', quantity: '1', unitCost: '0' }]);
  const [saving, setSaving] = useState(false);

  const supplier = suppliers.find((item) => item.id === supplierId);
  // Lo último que propuso la pantalla, para distinguirlo de lo que ha escrito el usuario. Va en estado
  // (no en `useRef`) porque el hint lo enseña y porque en StrictMode React repite las funciones de
  // actualización: aquí se decide en el manejador, con los valores del cierre, que es lo único seguro.
  const [propuestaFecha, setPropuestaFecha] = useState('');
  const [propuestaPago, setPropuestaPago] = useState('');
  const elegirProveedor = (id: string) => {
    setSupplierId(id);
    const elegido = suppliers.find((item) => item.id === id);
    if (!elegido) {
      // Volver a «Elige proveedor…» retira las propuestas, pero no lo que haya escrito el usuario.
      if (expectedDate === propuestaFecha) setExpectedDate('');
      if (paymentMethodId === propuestaPago) setPaymentMethodId('');
      const limpia = limpiarMoneda(currency, propuestaMoneda);
      setCurrency(limpia.currency);
      setPropuestaFecha('');
      setPropuestaPago('');
      setPropuestaMoneda('EUR');
      return;
    }

    // La moneda del proveedor se propone igual que la fecha y la forma de pago: si el usuario ya ha
    // elegido otra, se respeta la suya.
    const propuesta = proponerMoneda(currency, propuestaMoneda, elegido.currency);
    setCurrency(propuesta.currency);
    setPropuestaMoneda(propuesta.propuesta);

    const fecha = fechaPrevista(elegido.lead_time_days);
    if (expectedDate === '' || expectedDate === propuestaFecha) {
      setExpectedDate(fecha);
      setPropuestaFecha(fecha);
    } else {
      setPropuestaFecha('');
    }

    // Solo se propone una forma de pago que el desplegable pueda enseñar y que la API acepte; si la de
    // la ficha está desactivada, se deja vacía y el servidor aplica la suya sin pasar por el formulario.
    const deLaFicha = elegido.payment_method_id ?? '';
    const proponible = deLaFicha && formasPago.some((forma) => forma.id === deLaFicha) ? deLaFicha : '';
    if (paymentMethodId === '' || paymentMethodId === propuestaPago) {
      setPaymentMethodId(proponible);
      setPropuestaPago(proponible);
    } else {
      setPropuestaPago('');
    }
  };

  // Formas de pago de proveedor; vacío = la que tenga el proveedor en su ficha.
  const formasQuery = useQuery({
    queryKey: ['payment-methods', 'pagos'],
    queryFn: () => api<{ data: { id: string; name: string }[] }>('/payment-methods?scope=pagos'),
    staleTime: 10 * 60 * 1000
  });
  const formasPago = formasQuery.data?.data ?? [];
  const esImportacion = Boolean(supplier?.country && !UE.has(supplier.country));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        supplierId,
        paymentMethodId: paymentMethodId || null,
        currency,
        expectedDate: expectedDate || null,
        notes: notes || null,
        lines: lines
          .filter((line) => line.productId)
          .map((line) => ({ productId: line.productId, quantity: Number(line.quantity), unitCost: Number(line.unitCost) }))
      };
      if (!payload.supplierId) throw new Error('Elige un proveedor');
      if (payload.lines.length === 0) throw new Error('Añade al menos una línea');
      if (payload.lines.some((line) => !Number.isFinite(line.quantity) || line.quantity <= 0)) {
        throw new Error('Cada línea necesita una cantidad mayor que cero');
      }
      if (payload.lines.some((line) => !Number.isFinite(line.unitCost) || line.unitCost < 0)) {
        throw new Error('El coste no puede ser negativo');
      }
      await api('/purchase-orders', { method: 'POST', body: payload });
      toast.success('Pedido creado en borrador. Confírmalo para que cuente como material entrante.');
      onSaved();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-4">
      <div className="flex items-center justify-between border-b border-border p-3">
        <div>
          <p className="text-sm font-semibold">Nuevo pedido de compra</p>
          <p className="text-[11px] text-muted-foreground">
            Se crea en <strong>borrador</strong>: no cuenta como material entrante hasta que lo confirmes.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
      </div>
      <div className="grid gap-3 p-3 sm:grid-cols-3">
        <Field label="Proveedor">
          <Select value={supplierId} required onChange={(event) => elegirProveedor(event.target.value)}>
            <option value="">Elige proveedor…</option>
            {suppliers.map((item) => (
              <option key={item.id} value={item.id}>{item.name}{item.country ? ` · ${item.country}` : ''}</option>
            ))}
          </Select>
        </Field>
        <Field
          label="Fecha prevista de llegada"
          hint={propuestaFecha && expectedDate === propuestaFecha
            ? `Propuesta: hoy + ${supplier?.lead_time_days ?? 0} días (plazo del proveedor). Puedes cambiarla`
            : esImportacion ? 'Importación fuera de la UE: cuenta tránsito y aduana' : supplier?.country ? 'Proveedor de la UE: sin aduana' : undefined}
        >
          <Input type="date" value={expectedDate} onChange={(event) => {
            setPropuestaFecha('');
            setExpectedDate(event.target.value);
          }} />
        </Field>
        <CurrencyField value={currency} onChange={(code) => { setCurrency(code); setPropuestaMoneda(''); }}
          hint="La del proveedor; el tipo se fija hoy" />
        <Field label="Forma de pago" hint="Si no eliges, la de la ficha del proveedor">
          <Select value={paymentMethodId} onChange={(event) => setPaymentMethodId(event.target.value)}>
            <option value="">La del proveedor</option>
            {formasPago.map((forma) => (
              <option key={forma.id} value={forma.id}>{forma.name}</option>
            ))}
          </Select>
        </Field>
        <Field label="Notas">
          <Input value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Incoterm, contenedor, referencia del proveedor…" />
        </Field>
      </div>
      <div className="space-y-2 px-3 pb-3">
        {lines.map((line, index) => (
          <div key={index} className="grid gap-2 sm:grid-cols-[3fr_1fr_1fr_auto]">
            <Select
              value={line.productId}
              aria-label={`Artículo de la línea ${index + 1}`}
              onChange={(event) => setLines((current) => current.map((item, i) => i === index ? { ...item, productId: event.target.value } : item))}
            >
              <option value="">Artículo…</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.sku} · {product.name}{product.base_unit ? ` (${product.base_unit})` : ''}
                </option>
              ))}
            </Select>
            <Input
              type="number" min="0" step="0.001" value={line.quantity} aria-label={`Cantidad de la línea ${index + 1}`}
              onChange={(event) => setLines((current) => current.map((item, i) => i === index ? { ...item, quantity: event.target.value } : item))}
            />
            <Input
              type="number" min="0" step="0.01" value={line.unitCost} aria-label={`Coste unitario de la línea ${index + 1}`}
              onChange={(event) => setLines((current) => current.map((item, i) => i === index ? { ...item, unitCost: event.target.value } : item))}
            />
            <Button
              variant="ghost" size="icon" aria-label={`Quitar la línea ${index + 1}`} disabled={lines.length === 1}
              onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
            >
              <Trash2 />
            </Button>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setLines((current) => [...current, { productId: '', quantity: '1', unitCost: '0' }])}>
            <Plus /> Añadir línea
          </Button>
          <Button className="ml-auto" size="sm" loading={saving} onClick={() => void save()}>Crear pedido</Button>
        </div>
      </div>
    </Card>
  );
}

function OrderDetailPanel({ orderId, canReceive, canWrite, onChanged }: {
  orderId: string; canReceive: boolean; canWrite: boolean; onChanged: () => void;
}) {
  const [receive, setReceive] = useState<Record<string, string>>({});
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [notas, setNotas] = useState('');
  const [saving, setSaving] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['purchase-order', orderId],
    queryFn: () => api<OrderDetail>(`/purchase-orders/${orderId}`)
  });
  const order = detailQuery.data;
  const recibible = Boolean(order && ESTADOS_RECIBIBLES.includes(order.status));
  const formasQuery = useQuery({
    queryKey: ['payment-methods', 'pagos'],
    queryFn: () => api<{ data: { id: string; name: string }[] }>('/payment-methods?scope=pagos'),
    enabled: canWrite,
    staleTime: 10 * 60 * 1000
  });
  const formasPago = formasQuery.data?.data ?? [];

  /** Cambia la forma de pago del pedido ya creado (borrador o confirmado). */
  const [guardandoForma, setGuardandoForma] = useState(false);
  const cambiarFormaPago = async (paymentMethodId: string) => {
    setGuardandoForma(true);
    try {
      await api(`/purchase-orders/${orderId}`, {
        method: 'PATCH', body: { paymentMethodId: paymentMethodId || null }
      });
      toast.success('Forma de pago del pedido actualizada');
      await detailQuery.refetch();
      onChanged();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setGuardandoForma(false);
    }
  };

  /** La validación es por línea y se ve antes de enviar: antes se descartaban en silencio. */
  const validar = (line: LineRow, valor: string): string => {
    if (valor === '') return '';
    const numero = Number(valor);
    if (!Number.isFinite(numero)) return 'No es un número';
    if (numero <= 0) return 'Tiene que ser mayor que cero';
    const pendiente = line.quantity - line.receivedQuantity;
    if (numero > pendiente) return `Como mucho ${formatQty(pendiente)}`;
    return '';
  };

  const submit = async () => {
    if (!order) return;
    const nuevosErrores: Record<string, string> = {};
    const lines: { lineId: string; quantity: number }[] = [];
    for (const line of order.lines) {
      const valor = receive[line.id];
      if (valor === undefined || valor === '') continue;
      const error = validar(line, valor);
      if (error) { nuevosErrores[line.id] = error; continue; }
      lines.push({ lineId: line.id, quantity: Number(valor) });
    }
    setErrores(nuevosErrores);
    if (Object.keys(nuevosErrores).length > 0) {
      toast.error('Revisa las cantidades marcadas');
      return;
    }
    if (lines.length === 0) {
      toast.error('Indica cuánto has recibido en al menos una línea');
      return;
    }
    setSaving(true);
    try {
      const result = await api<{ number?: string }>(`/purchase-orders/${orderId}/receive`, {
        method: 'POST', body: { lines, notes: notas || null }
      });
      toast.success(result?.number ? `Recepción ${result.number} registrada` : 'Recepción registrada');
      setReceive({});
      setNotas('');
      await detailQuery.refetch();
      onChanged();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const cambiarEstado = async (accion: 'confirm' | 'cancel') => {
    setSaving(true);
    try {
      await api(`/purchase-orders/${orderId}/${accion}`, { method: 'POST', body: {} });
      toast.success(accion === 'confirm' ? 'Pedido confirmado: ya cuenta como material entrante' : 'Pedido cancelado');
      await detailQuery.refetch();
      onChanged();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (detailQuery.isLoading) {
    return <Card className="mt-4 p-4 text-sm text-muted-foreground">Cargando pedido…</Card>;
  }
  if (detailQuery.error || !order) {
    return (
      <Card className="mt-4">
        <ErrorState message={(detailQuery.error as Error)?.message ?? 'Error'} onRetry={() => void detailQuery.refetch()} />
      </Card>
    );
  }

  const hayPendiente = order.lines.some((line) => line.quantity - line.receivedQuantity > 0);

  return (
    <Card className="mt-4">
      <div className="flex flex-wrap items-center gap-3 border-b border-border p-3">
        <div className="flex-1">
          <p className="font-mono text-sm font-semibold">{order.number}</p>
          <p className="text-[11px] text-muted-foreground">
            {order.supplierName} · prevista {order.expectedDate ? formatDate(order.expectedDate) : 'sin fecha'}
            {order.currency && order.currency !== 'EUR'
              ? (order.exchangeRate
                ? ` · ${order.currency} (1 ${order.currency} = ${Number(order.exchangeRate).toLocaleString('es-ES', { maximumFractionDigits: 6 })} €)`
                : ` · ${order.currency} (sin tipo)`)
              : ''}
            {order.notes ? ` · ${order.notes}` : ''}
          </p>
          {/* La forma de pago se fija al crear el pedido y aquí se puede corregir mientras siga abierto. */}
          {canWrite && !['received', 'cancelled'].includes(order.status) ? (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">Forma de pago</span>
              <Select
                value={order.paymentMethodId ?? ''}
                className="h-7 w-56 text-xs"
                aria-label="Forma de pago del pedido"
                disabled={formasQuery.isLoading || guardandoForma}
                onChange={(event) => void cambiarFormaPago(event.target.value)}
              >
                <option value="">La del proveedor</option>
                {/* Si la forma asignada ya no está activa, se pinta igual: si no, el desplegable
                    diría «La del proveedor» y al tocarlo se perdería la que hay de verdad. */}
                {order.paymentMethodId && !formasPago.some((forma) => forma.id === order.paymentMethodId) ? (
                  <option value={order.paymentMethodId}>{order.paymentMethodName ?? 'La asignada'} (inactiva)</option>
                ) : null}
                {formasPago.map((forma) => (
                  <option key={forma.id} value={forma.id}>{forma.name}</option>
                ))}
              </Select>
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground">
              Forma de pago: {order.paymentMethodName ?? 'la del proveedor'}
            </p>
          )}
        </div>
        <StatusBadge status={order.status} />
        {canWrite && order.status === 'draft' ? (
          <Button size="sm" variant="outline" loading={saving} onClick={() => void cambiarEstado('confirm')}>
            <CheckCircle2 /> Confirmar
          </Button>
        ) : null}
        {canWrite && ESTADOS_RECIBIBLES.includes(order.status) ? (
          <Button size="sm" variant="ghost" className="text-destructive" loading={saving} onClick={() => void cambiarEstado('cancel')}>
            <XCircle /> Cancelar
          </Button>
        ) : null}
      </div>

      <ul className="divide-y divide-border">
        {order.lines.map((line) => {
          const pendiente = line.quantity - line.receivedQuantity;
          const error = errores[line.id] ?? '';
          return (
            <li key={line.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5 text-sm">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">
                  <span className="font-mono">{line.sku ?? '—'}</span>
                  {line.productName ? ` · ${line.productName}` : ''}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Pedido {formatQty(line.quantity)} {line.purchaseUnit ?? ''} · recibido {formatQty(line.receivedQuantity)}
                  {' · '}pendiente {formatQty(pendiente)}
                  {line.purchaseToBase !== 1 && line.baseUnit ? ` · entra en ${line.baseUnit} (×${line.purchaseToBase})` : ''}
                </p>
                {error ? <p className="text-[11px] font-medium text-destructive">{error}</p> : null}
              </div>
              <span className="tabular-nums text-muted-foreground">{formatMoney(line.unitCost)}</span>
              {canReceive && recibible && pendiente > 0 ? (
                <Input
                  type="number" min="0" max={pendiente} step="0.001"
                  className={cn('w-28', error && 'border-destructive')}
                  aria-label={`Cantidad recibida de ${line.sku ?? 'la línea'}`}
                  aria-invalid={Boolean(error)}
                  placeholder={`máx ${formatQty(pendiente)}`}
                  value={receive[line.id] ?? ''}
                  onChange={(event) => {
                    const valor = event.target.value;
                    setReceive((current) => ({ ...current, [line.id]: valor }));
                    setErrores((current) => ({ ...current, [line.id]: validar(line, valor) }));
                  }}
                />
              ) : null}
            </li>
          );
        })}
      </ul>

      {canReceive && recibible && hayPendiente ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-border p-3">
          <p className="mr-auto text-[11px] text-muted-foreground">
            Entra en stock por la ubicación por defecto del almacén y con asiento propio. Cuando llegue la
            factura, enlázala con este pedido: una factura enlazada solo comprueba cantidades y actualiza
            precios, no vuelve a mover stock. La entrada solo se duplicaría si la validases como compra
            directa o como mercancía no pedida.
          </p>
          <Input
            value={notas} onChange={(event) => setNotas(event.target.value)}
            placeholder="Albarán del proveedor / notas" className="w-64" aria-label="Albarán del proveedor o notas"
          />
          <Button size="sm" loading={saving} onClick={() => void submit()}>Registrar recepción</Button>
        </div>
      ) : null}
    </Card>
  );
}
